import type {
  ClassificationLevelDetail,
  FunctionClassificationEntry,
} from '../../utils/monitoringClassification';
import { formatScoreAsPercentage } from '../../utils/monitoringClassification';

const LEVEL_LABELS = ['Domain', 'Subdomain', 'Function'] as const;

function hasDescription(level: ClassificationLevelDetail): boolean {
  return Boolean(level.description && level.description !== '—');
}

function LevelSection({
  label,
  level,
}: {
  label: string;
  level: ClassificationLevelDetail;
}) {
  return (
    <div
      className="rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2.5 space-y-1.5"
      onClick={(e) => e.stopPropagation()}
    >
      <p className="text-sm font-medium text-gray-800">
        {label}
        {level.name !== '—' && (
          <span className="ml-1.5 font-normal text-gray-500">— {level.name}</span>
        )}
      </p>
      {hasDescription(level) && (
        <p className="text-xs leading-relaxed text-gray-600">
          <span className="font-medium text-gray-500">Description: </span>
          {level.description}
        </p>
      )}
    </div>
  );
}

function ClassificationEntryPanel({ entry }: { entry: FunctionClassificationEntry }) {
  return (
    <div
      className={`rounded-lg border px-3 py-3 space-y-2 ${
        entry.isSelected
          ? 'border-emerald-200 bg-emerald-50/30'
          : 'border-gray-200 bg-white'
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 border-b border-gray-100 pb-2">
        <p className="text-sm text-gray-800 shrink-0">
          <span className="font-medium text-gray-500">Ranking score: </span>
          <span className="font-mono font-semibold">{formatScoreAsPercentage(entry.rankingScore)}</span>
        </p>
      </div>
      {entry.isSelected && (
        <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
          Selected classification
        </span>
      )}

      <div className="space-y-1.5 pt-1">
        {entry.levels.map((level, index) => (
          <LevelSection key={LEVEL_LABELS[index]} label={LEVEL_LABELS[index]} level={level} />
        ))}
      </div>
    </div>
  );
}

export default function FunctionClassificationLevelBlock({
  entries,
}: {
  entries: FunctionClassificationEntry[];
}) {
  return (
    <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
      {entries.map((entry) => (
        <ClassificationEntryPanel key={entry.classificationId} entry={entry} />
      ))}
    </div>
  );
}
